/**
 * Strategy Templates
 * Preset lending bot configurations (Conservative, Balanced, Aggressive)
 */

export interface StrategyTemplate {
  id: string
  name: string
  description: string
  risk_level: 'low' | 'medium' | 'high'
  config: {
    min_daily_rate: number
    gap_mode: 'raw' | 'rawbtc' | 'relative'
    gap_bottom: number
    gap_top: number
    spread_lend: number
    min_loan_size: number
    max_active_amount?: number
    max_percent_to_lend?: number
    xday_threshold?: number
    xdays?: number
    auto_transfer: boolean
    update_interval: number
  }
  expected_apr: string
  suitable_for: string
}

/**
 * Conservative Template (保守型)
 * - 低風險，穩定收益
 * - 較高的利率要求
 * - 較少的放貸金額
 */
export const CONSERVATIVE_TEMPLATE: StrategyTemplate = {
  id: 'conservative',
  name: '保守型',
  description: '低風險策略，追求穩定收益',
  risk_level: 'low',
  config: {
    min_daily_rate: 0.0003, // 0.03% daily = ~11% APR
    gap_mode: 'relative',
    gap_bottom: 5.0, // Top 5% of orderbook
    gap_top: 15.0, // Top 15% of orderbook
    spread_lend: 5, // 5 offers
    min_loan_size: 150,
    max_percent_to_lend: 60.0, // Only lend 60% of balance
    xday_threshold: 0.005, // Lock rates > 0.5%/day
    xdays: 30, // Lock for 30 days
    auto_transfer: false,
    update_interval: 600, // 10 minutes
  },
  expected_apr: '10-15%',
  suitable_for: '適合新手，追求穩定收益的用戶',
}

/**
 * Balanced Template (均衡型)
 * - 中等風險，平衡收益
 * - 適中的利率要求
 * - 分散風險策略
 */
export const BALANCED_TEMPLATE: StrategyTemplate = {
  id: 'balanced',
  name: '均衡型',
  description: '平衡風險與收益的策略',
  risk_level: 'medium',
  config: {
    min_daily_rate: 0.0002, // 0.02% daily = ~7.3% APR
    gap_mode: 'relative',
    gap_bottom: 2.0, // Top 2% of orderbook
    gap_top: 20.0, // Top 20% of orderbook
    spread_lend: 10, // 10 offers for better diversification
    min_loan_size: 100,
    max_percent_to_lend: 80.0, // Lend 80% of balance
    xday_threshold: 0.003, // Lock rates > 0.3%/day
    xdays: 15, // Lock for 15 days
    auto_transfer: false,
    update_interval: 600, // 10 minutes
  },
  expected_apr: '7-12%',
  suitable_for: '適合有經驗的用戶，追求平衡收益',
}

/**
 * Aggressive Template (激進型)
 * - 高風險，最大化收益
 * - 較低的利率要求（快速成交）
 * - 最大化資金利用率
 */
export const AGGRESSIVE_TEMPLATE: StrategyTemplate = {
  id: 'aggressive',
  name: '激進型',
  description: '高風險策略，最大化資金利用率',
  risk_level: 'high',
  config: {
    min_daily_rate: 0.0001, // 0.01% daily = ~3.65% APR
    gap_mode: 'relative',
    gap_bottom: 1.0, // Top 1% of orderbook (very competitive)
    gap_top: 30.0, // Top 30% of orderbook
    spread_lend: 15, // 15 offers for maximum diversification
    min_loan_size: 50,
    max_percent_to_lend: 95.0, // Lend 95% of balance
    xday_threshold: 0.002, // Lock rates > 0.2%/day
    xdays: 7, // Lock for 7 days (shorter lock)
    auto_transfer: true, // Auto-transfer from exchange wallet
    update_interval: 300, // 5 minutes (more frequent updates)
  },
  expected_apr: '5-10%',
  suitable_for: '適合進階用戶，追求最大化資金利用率',
}

/**
 * All available templates
 */
export const STRATEGY_TEMPLATES: StrategyTemplate[] = [
  CONSERVATIVE_TEMPLATE,
  BALANCED_TEMPLATE,
  AGGRESSIVE_TEMPLATE,
]

/**
 * Get template by ID
 */
export function getTemplateById(id: string): StrategyTemplate | undefined {
  return STRATEGY_TEMPLATES.find((t) => t.id === id)
}

/**
 * Get default template (balanced)
 */
export function getDefaultTemplate(): StrategyTemplate {
  return BALANCED_TEMPLATE
}
